/**
 * The Platform Users screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/platform-members` (all optional; a default is never written):
 * - `tab`: `members` (default) | `invited`.
 * - `q`: the active tab's search (Members: name or email; Invited: email).
 * - `sort` + `dir`: the active tab's column sort. Members keeps its existing default,
 *   Email ascending; Invited defaults to Invited, newest first (the server order is not
 *   defined), matching the Users screen's Invited tab.
 * A tab change clears `q`, `sort` and `dir`, because each tab has its own columns.
 *
 * Blanks (no name, no role, no status, no date) sort last in BOTH directions; the old
 * private sort reversed the whole list for descending, which floated blanks to the top.
 */
import type { PlatformInvitation, PlatformMember } from '@/api/platformMembers';
import { matchesSearch } from '@/components/list/listSearch';
import { toTime, type SortState, type SortValue } from '@/components/list/listSort';
import { rosterDisplayRole } from '@/domain/rosterKind';
import { roleBadge, statusBadge } from './platformBadges';

/** The screen tabs: the staff roster vs. the pending elevated invitations. */
export type ScreenTab = 'members' | 'invited';
export const SCREEN_TABS: readonly ScreenTab[] = ['members', 'invited'];

/** Every param the screen keeps in the URL, with its default. */
export const PLATFORM_MEMBERS_URL_DEFAULTS = { tab: 'members', q: '', sort: '', dir: '' };

// --- Members -------------------------------------------------------------------------

export type MemberSortKey = 'name' | 'email' | 'role' | 'status';
export const MEMBER_SORT_KEYS: readonly MemberSortKey[] = ['name', 'email', 'role', 'status'];
export const MEMBER_DEFAULT_SORT: SortState<MemberSortKey> = { key: 'email', dir: 'asc' };

/**
 * What a roster row shows (and sorts by): the name, the email, the Role badge label (from
 * the displayed role) and the Status badge label. A blank cell (the placeholder) sorts
 * with the blanks.
 */
export function memberSortValue(
  member: PlatformMember,
  key: MemberSortKey,
  staffLinked = false,
): SortValue {
  switch (key) {
    case 'name':
      return member.name;
    case 'email':
      return member.email;
    case 'role': {
      const role = rosterDisplayRole(member);
      return role ? roleBadge(role, 'light', staffLinked).label : null;
    }
    case 'status':
      return member.status ? statusBadge(member.status).label : null;
  }
}

/** The Members search: the name or the email. */
export function memberMatches(member: PlatformMember, query: string): boolean {
  return matchesSearch(query, [member.name, member.email]);
}

// --- Invited -------------------------------------------------------------------------

export type InvitedSortKey = 'email' | 'role' | 'invitedBy' | 'invited' | 'expires';
export const INVITED_SORT_KEYS: readonly InvitedSortKey[] = [
  'email',
  'role',
  'invitedBy',
  'invited',
  'expires',
];
export const INVITED_DEFAULT_SORT: SortState<InvitedSortKey> = { key: 'invited', dir: 'desc' };

/**
 * What an invitation row shows (and sorts by). `invitedBy` is the row's resolved "Invited
 * by" text (a roster name, else the raw id), or `null` when unknown.
 */
export function invitedSortValue(
  invitation: PlatformInvitation,
  key: InvitedSortKey,
  invitedBy: string | null,
): SortValue {
  switch (key) {
    case 'email':
      return invitation.email;
    case 'role':
      return invitation.role ? roleBadge(invitation.role).label : null;
    case 'invitedBy':
      return invitedBy;
    case 'invited':
      return toTime(invitation.createdAt);
    case 'expires':
      return toTime(invitation.expiresAt);
  }
}

/** The Invited search: the email. */
export function invitedMatches(invitation: PlatformInvitation, query: string): boolean {
  return matchesSearch(query, [invitation.email]);
}

/**
 * Whether the row offers the location-access Edit button (DNKADM-0474). An Owner is always
 * All, and so is an Admin whose access already reads All, so Edit there changes nothing. An
 * Admin the server still holds a specific list for keeps it (DNKADM-0453), so that list can be
 * widened. Display only; the server enforces.
 */
export function canEditLocationAccess(member: PlatformMember): boolean {
  if (member.role === 'OWNER') return false;
  return !(rosterDisplayRole(member) === 'ADMIN' && member.locationAccess.allLocations);
}
